import React, { useState, useEffect, useMemo } from "react";
import { birthdayInfo, formatBirthday } from "../birthdays";
import "./BirthdayAlert.css";

// Static confetti pieces — generated once, animated via CSS.
const CONFETTI_COLORS = ["#f5a623", "#e05c5c", "#4d9ef7", "#3ecf8e", "#a78bfa", "#F48FB1", "#26C6DA"];
const CONFETTI = Array.from({ length: 60 }, (_, i) => ({
  left: Math.random() * 100,
  delay: -Math.random() * 4,
  dur: 3 + Math.random() * 3,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  size: 6 + Math.random() * 8,
  rot: Math.random() * 360,
}));

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export default function BirthdayAlert({ workers = [] }) {
  const [open, setOpen] = useState(false);

  const { todays, upcoming } = useMemo(() => {
    const now = new Date();
    const withInfo = workers
      .map(w => { const i = birthdayInfo(w.birthday, now); return i ? { ...w, ...i } : null; })
      .filter(Boolean);
    return {
      todays: withInfo.filter(w => w.daysUntil === 0).sort((a, b) => a.lastName?.localeCompare(b.lastName)),
      upcoming: withInfo.filter(w => w.daysUntil >= 1 && w.daysUntil <= 3).sort((a, b) => a.daysUntil - b.daysUntil),
    };
  }, [workers]);

  // Fire once per day (per browser), only when there's something to celebrate.
  useEffect(() => {
    if (!todays.length && !upcoming.length) return;
    if (sessionStorage.getItem("bdayAlertSeen") === todayKey()) return;
    setOpen(true);
  }, [todays.length, upcoming.length]);

  const close = () => {
    sessionStorage.setItem("bdayAlertSeen", todayKey());
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="bda-overlay" role="dialog" aria-modal="true" aria-label="Birthday alert">
      <div className="bda-confetti" aria-hidden="true">
        {CONFETTI.map((c, i) => (
          <span key={i} className="bda-piece" style={{
            left: `${c.left}%`, background: c.color,
            width: `${c.size}px`, height: `${c.size * 0.42}px`,
            animationDelay: `${c.delay}s`, animationDuration: `${c.dur}s`,
            "--rot": `${c.rot}deg`,
          }} />
        ))}
      </div>

      <div className="bda-box">
        <button className="bda-x" onClick={close} aria-label="Dismiss">✕</button>
        <div className="bda-emoji">{todays.length ? "🎂" : "🎉"}</div>

        {todays.length > 0 ? (
          <>
            <h2 className="bda-title">
              {todays.length === 1 ? "It's a birthday today!" : `${todays.length} birthdays today!`}
            </h2>
            <div className="bda-today-list">
              {todays.map(w => (
                <div key={w.id} className="bda-hero">
                  🎉 <strong>{w.firstName} {w.lastName}</strong>
                  <span className="bda-hero-sub">{w.role ? `${w.role} · ` : ""}turning {w.turning}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <h2 className="bda-title">Birthdays coming up! 🎈</h2>
        )}

        {upcoming.length > 0 && (
          <div className="bda-upcoming">
            <div className="bda-upcoming-label">In the next 3 days</div>
            {upcoming.map(w => (
              <div key={w.id} className="bda-row">
                <span className="bda-row-name">{w.firstName} {w.lastName}</span>
                <span className="bda-row-when">
                  {formatBirthday(w.month, w.day)} · {w.daysUntil === 1 ? "tomorrow" : `in ${w.daysUntil} days`} · turns {w.turning}
                </span>
              </div>
            ))}
          </div>
        )}

        <button className="bda-btn" onClick={close}>
          {todays.length ? "🎉 Let's celebrate!" : "Got it 🎈"}
        </button>
        <div className="bda-hint">Reach out and make their day.</div>
      </div>
    </div>
  );
}
